import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import "react-multi-email/dist/style.css";
import {Drawer} from "../../../components/Modals";
import CustomButton from "../../../components/common/CustomButton";
import {useJwt} from "../../../helpers/common";
import useOrganizationService from "../../../services/organization";
import {Avatar} from "flowbite-react";
import moment from "moment";
import ModernEmailInput from "../../../components/common/ModernEmailInput";
import { UserRole } from "../../../types/manageUsers";

interface InviteTeamModalProps {
  isVisible: boolean;
  onClose: () => void;
}

enum TabType {
  PENDING = "pending",
  INVITE = "invite",
}

const InviteTeamModal: React.FC<InviteTeamModalProps> = ({
  isVisible,
  onClose,
}) => {
  const JWTdata: any = useJwt();
  const [selected, setSelected] = useState<{ email: string; role: UserRole }[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>(TabType.INVITE);
  const { inviteUsers, fetchPendingInvites, revokeInvite } =
    useOrganizationService();
  const {
    inviteUsersLoading,
    pendingInvites,
    pendingInvitesLoading,
    revokeInviteLoading,
  } = useSelector((state: any) => state.organization);
  const { data: resources } = useSelector((state: any) => state.resources);

  // Separate active members vs pending/expired invites
  // Only block duplicates for NON-imported active users
  const activeMemberEmailSet = useMemo(() => {
    if (!resources || resources.length === 0) return new Set<string>();
    return new Set<string>(
      resources
        .filter((res: any) =>
            (res?.imported !== true)
            && res?.invite_status !== "pending"
            && res?.invite_status !== "expired"
        )
        .map((res: any) => (res?.email || "").toLowerCase())
    );
  }, [resources]);

  const pendingInviteEmailSet = useMemo(() => {
    if (!pendingInvites || pendingInvites.length === 0)
      return new Set<string>();
    return new Set<string>(
      pendingInvites.map((res: any) => (res?.email || "").toLowerCase())
    );
  }, [pendingInvites]);

  // Memoised duplicate detection so UI updates whenever selection changes
  const duplicateActiveEmails = useMemo(() => {
    return selected.filter((item) =>
      activeMemberEmailSet.has(item.email.toLowerCase())
    );
  }, [selected, activeMemberEmailSet]);

  const duplicateAnyEmails = useMemo(() => {
    return [...duplicateActiveEmails];
  }, [duplicateActiveEmails]);

  // Handler for ReactMultiEmail change
  const handleEmailsChange = (_emails: string[]) => {
    const newEmailList = _emails.map(email => {
      const existing = selected.find(item => item.email === email);
      return existing || { email, role: UserRole.BASIC_MEMBER };
    });
    setSelected(newEmailList);
  };

  const handleRoleChange = (email: string, role: UserRole) => {
    setSelected(prevList =>
      prevList.map(item => (item.email === email ? { ...item, role } : item))
    );
  };

  const handleContinue = async () => {
    if (duplicateAnyEmails.length > 0) return; // should be disabled already but double-check

    if (JWTdata?.org_id && selected.length) {
      if (!inviteUsersLoading) {
        const payload = {
          email_list: selected.map(item => ({ email: item.email, role: item.role }))
        };
        await inviteUsers(JWTdata.org_id, payload);
        setSelected([]);
        setActiveTab(TabType.PENDING);
      }
    }
  };
  // Fetch pending invites when modal opens
  useEffect(() => {
    if (isVisible && JWTdata?.org_id) {
      fetchPendingInvites(JWTdata.org_id);
    }
  }, [isVisible, JWTdata?.org_id]);

  // Refresh pending invites when switching to pending tab
  useEffect(() => {
    if (activeTab === TabType.PENDING && JWTdata?.org_id && isVisible) {
      fetchPendingInvites(JWTdata.org_id);
    }
  }, [activeTab]);

  const handleClose = () => {
    setSelected([]);
    setActiveTab(TabType.INVITE);
    onClose();
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  const renderPendingInvitesSkeleton = () => {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, index) => (
          <div
            key={index}
            className="flex items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 animate-pulse"
          >
            {/* Avatar Skeleton */}
            <div className="w-10 h-10 rounded-full mr-3 bg-gray-200 dark:bg-gray-700"></div>

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {/* Name Skeleton */}
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                {/* Status Badge Skeleton */}
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-full w-16"></div>
              </div>
              {/* Email Skeleton */}
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleRevokeInvite = async (inviteId: string) => {
    if (JWTdata?.org_id && inviteId) {
      await revokeInvite(JWTdata.org_id, inviteId);
    }
  };

  const formatInviteDate = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "";

    let inviteDate;

    // Try to determine if timestamp is in seconds or milliseconds
    // If timestamp is greater than year 2100 in seconds (4102444800), it's likely milliseconds
    if (timestamp > 4102444800) {
      inviteDate = moment(timestamp); // milliseconds
    } else {
      inviteDate = moment.unix(timestamp); // seconds
    }

    // Check if the date is valid
    if (!inviteDate.isValid()) {
      return "";
    }

    const now = moment();
    const daysDiff = now.diff(inviteDate, "days");

    // If the date is in the future or too far in the past, just show the formatted date
    if (daysDiff < 0 || daysDiff > 365) {
      return `on ${inviteDate.format("MMM DD, YYYY")}`;
    }

    if (daysDiff === 0) {
      return "today";
    } else if (daysDiff === 1) {
      return "yesterday";
    } else if (daysDiff < 7) {
      return `${daysDiff} days ago`;
    } else if (daysDiff < 30) {
      const weeksDiff = Math.floor(daysDiff / 7);
      return weeksDiff === 1 ? "1 week ago" : `${weeksDiff} weeks ago`;
    } else {
      return `on ${inviteDate.format("MMM DD, YYYY")}`;
    }
  };

  const renderPendingInvites = () => {
    if (pendingInvitesLoading) {
      return renderPendingInvitesSkeleton();
    }

    if (pendingInvites.length === 0) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              No pending invites found
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              All invitations have been accepted or expired
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {pendingInvites.map((invite: any, index: number) => (
          <div
            key={invite?.invite_id || index}
            className="flex items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div className="flex-shrink-0 mr-3">
              <Avatar
                className="w-10 h-10 rounded-full"
                img={invite?.image_url}
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {invite?.name || invite?.email || "Unknown User"}
                </p>

                {invite?.invite_status === "pending" && (
                  <span className="px-2 py-1 text-xs rounded-full capitalize bg-yellow-100 text-yellow-800">
                    Pending
                  </span>
                )}

                {invite?.invite_status === "expiring" && (
                  <span className="px-2 py-1 text-xs rounded-full capitalize bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                    Expired
                  </span>
                )}
              </div>

              {invite?.invite_created_at && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Invitation sent {formatInviteDate(invite.invite_created_at)}
                </p>
              )}
            </div>

            {invite?.invite_status === "pending" && (
              <button
                onClick={() => handleRevokeInvite(invite?.invite_id)}
                disabled={revokeInviteLoading}
                className="ml-3 p-1 px-2 text-red-600  dark:text-red-400  hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Revoke invitation"
              >
                <span className="text-red-600 text-sm hover:text-red-800 dark:text-red-400 ">
                Revoke 
                </span>
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Drawer isOpen={isVisible} onClose={handleClose} position="right">
      <div className="h-full bg-tertiary-light dark:bg-tertiary-dark flex flex-col">
        {/* Header with rounded border and shadow */}
        <div className="border-b border-gray-500 rounded-b-2xl dark:border-gray-700 px-6 py-6 bg-secondary-light dark:bg-secondary-dark shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-light-text-primary dark:text-white">
                Manage Invitations
              </h2>
              <p className="text-sm text-light-text-secondary dark:text-gray-400 mt-1">
                Invite new people or manage pending invitations
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-light-interactive-hover dark:hover:bg-hover-dark rounded-lg transition-all duration-200"
            >
              <svg
                className="w-5 h-5 text-light-text-secondary dark:text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {/* Tab Switcher with Animation */}
            <div className="mb-6">
              <div className="relative flex w-[99%] p-1 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                {/* Sliding Background Indicator */}
                <div
                  className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white dark:bg-gray-700 rounded-md shadow-sm border border-gray-200 dark:border-gray-600 transition-all duration-300 ease-in-out transform ${
                    activeTab === TabType.INVITE
                      ? "translate-x-0"
                      : "translate-x-[calc(100%)]"
                  }`}
                />

                <button
                  type="button"
                  onClick={() => handleTabChange(TabType.INVITE)}
                  className={`relative z-10 flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-300 ease-in-out focus:outline-none transform hover:scale-[1.02] ${
                    activeTab === TabType.INVITE
                      ? "text-gray-900 dark:text-white font-semibold"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  <svg
                    className={`w-4 h-4 mr-2 transition-transform duration-300 ${
                      activeTab === TabType.INVITE ? "scale-110" : "scale-100"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 4v16m8-8H4"
                    ></path>
                  </svg>
                  Invite New People
                </button>

                <button
                  type="button"
                  onClick={() => handleTabChange(TabType.PENDING)}
                  className={`relative z-10 flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-300 ease-in-out focus:outline-none transform hover:scale-[1.02] ${
                    activeTab === TabType.PENDING
                      ? "text-gray-900 dark:text-white font-semibold"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  <svg
                    className={`w-4 h-4 mr-2 transition-transform duration-300 ${
                      activeTab === TabType.PENDING ? "scale-110" : "scale-100"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  Pending Invites
                  {pendingInvites.length > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                      {pendingInvites.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Content Area with Transition */}
            <div className="relative min-h-[400px]">
              {/* Pending Invites Tab Content */}
              <div
                className={`absolute inset-0 transition-all duration-300 ease-in-out ${
                  activeTab === TabType.PENDING
                    ? "opacity-100 translate-x-0 pointer-events-auto"
                    : "opacity-0 translate-x-4 pointer-events-none"
                }`}
              >
                <div className="space-y-6">
                  <div className="mb-6 bg-light-status-warning-bg dark:bg-yellow-900/30 p-4 rounded-lg border border-light-status-warning-border dark:border-yellow-600">
                    <h3 className="text-sm font-medium text-light-status-warning-text dark:text-yellow-300 mb-1">
                      Pending Invitations
                    </h3>
                    <p className="text-sm text-light-text-secondary dark:text-gray-400">
                      Manage pending and expired invitations sent to people.
                    </p>
                  </div>
                  {renderPendingInvites()}
                </div>
              </div>

              <div
                className={`absolute inset-0 transition-all duration-300 ease-in-out ${
                  activeTab === TabType.INVITE
                    ? "opacity-100 translate-x-0 pointer-events-auto"
                    : "opacity-0 -translate-x-4 pointer-events-none"
                }`}
              >
                <div className="space-y-6">
                  {/* Email Input Section */}
                  <div className=" p-2">
                    <ModernEmailInput
                      emails={selected.map(item => item.email)}
                      onChange={handleEmailsChange}
                      existingEmails={activeMemberEmailSet}
                      pendingEmails={pendingInviteEmailSet}
                      placeholder="Enter email address..."
                      className="w-full"
                      disabled={inviteUsersLoading}
                      showRoles={true}
                      onRoleChange={handleRoleChange}
                      emailRoles={selected.reduce((acc, curr) => ({ ...acc, [curr.email]: curr.role }), {})}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="sticky bottom-0 left-0 right-0 p-4 bg-secondary-light dark:bg-secondary-dark border-t border-gray-500 rounded-t-md dark:border-gray-700 flex justify-between items-center shadow-lg z-10">
          <CustomButton
            onClick={handleClose}
            variant="cancel"
            disabled={inviteUsersLoading}
          >
            Cancel
          </CustomButton>

          {activeTab === TabType.INVITE && (
            <CustomButton
              onClick={handleContinue}
              disabled={
                inviteUsersLoading ||
                selected.length === 0 ||
                duplicateAnyEmails.length > 0
              }
              isLoading={inviteUsersLoading}
              variant="primary"
              className="bg-primary-light hover:bg-primary-hover"
            >
              Send Invites
            </CustomButton>
          )}
        </div>

        {/* Loading Overlay - For invite sending and revoke actions */}
        {(inviteUsersLoading || revokeInviteLoading) && (
          <div className="absolute z-10 bottom-0 top-0 flex items-center flex-col justify-center w-full h-full bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-xl flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-light dark:border-primary-dark mb-4"></div>
              <div className="text-center text-gray-900 dark:text-white text-base font-medium">
                {inviteUsersLoading
                  ? "Sending invitations..."
                  : "Revoking invitation..."}
              </div>
              <div className="text-center text-gray-500 dark:text-gray-400 text-sm mt-1">
                Please wait while we process your request
              </div>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default InviteTeamModal;
